<template>
    <div class="shift-copy">
        <!-- Подтверждение замены: только если preview сообщил об удалениях. -->
        <div v-if="mode === 'confirm' && preview" class="shift-copy-confirm">
            <div class="shift-copy-confirm-title">Скопировать смены дня в {{ formatDateList(modelValue) }}?</div>
            <div class="shift-copy-confirm-counts">
                <span>Будет создано <b>{{ formatShiftsCount(preview.total_shifts_to_create) }}</b></span>
                <span>Будет удалено <b>{{ formatShiftsCount(preview.total_shifts_to_delete) }}</b></span>
            </div>
            <div v-if="selectedOccupiedDates.length" class="shift-copy-muted">
                Удалятся смены в днях: {{ formatDateList(selectedOccupiedDates) }}
            </div>
            <div v-for="employment in skippedEmployments" :key="employment.employment_id" class="shift-copy-muted">
                Не будут скопированы — {{ employment.employee_name }}: {{ employment.errors.map(error => error.message).join(', ') }}
            </div>
        </div>

        <template v-else>
            <div v-if="mode === 'errors' && errors.length" role="alert" class="shift-copy-error">
                <span class="shift-copy-error-title">Не скопировано</span>
                <span v-for="(error, index) in errors" :key="`${error.code}-${index}`">{{ formatCopyError(error) }}</span>
            </div>

            <div v-else class="shift-copy-muted">{{ compositionText }}</div>

            <div class="shift-copy-calendar" :class="{ 'shift-copy-calendar-locked': isLoading }" :aria-busy="isLoading">
                <AppCalendar
                    :model-value="selectedDays"
                    mode="multi"
                    :source-dates="[sourceDay]"
                    :occupied-dates="occupiedDates"
                    :hr-deviation-dates="hrDeviationDates"
                    :hr-deviation-labels="hrDeviationLabels"
                    :min-date="minimumTargetDate"
                    :initial-date="store.currentScheduleDate"
                    :day-labels="dayLabels"
                    :outside-days-selectable="false"
                    large
                    copy-state-styles
                    @update:model-value="onMultiSelect"
                />
            </div>

            <!-- Легенда пяти состояний ячейки (RD-160). -->
            <div class="shift-copy-legend" aria-hidden="true">
                <span><i class="shift-copy-legend-mark shift-copy-legend-source" />копируемый день</span>
                <span><i class="shift-copy-legend-mark shift-copy-legend-add" />добавится смена</span>
                <span><i class="shift-copy-legend-mark shift-copy-legend-replace" />смена заменится</span>
                <span><i class="shift-copy-legend-time">09–18</i>уже есть смена</span>
                <span><i class="shift-copy-legend-chip">КС</i>кадровое состояние</span>
            </div>

            <!-- Итог до отправки — локальная сводка по загруженному графику; preview сервера остаётся обязательным. -->
            <div v-if="modelValue.length" class="shift-copy-summary">
                <div v-if="addDates.length" class="shift-copy-summary-row">
                    <div class="shift-copy-summary-head">
                        <span class="shift-copy-summary-title">Добавится</span>
                        <span class="shift-copy-summary-count">{{ formatDaysCount(addDates.length) }}</span>
                    </div>
                    <div class="shift-copy-summary-main">
                        <span>{{ formatDateRanges(addDates) }}</span>
                        <span class="shift-copy-muted">{{ newComposition }}</span>
                    </div>
                </div>
                <div v-if="replaceDates.length" class="shift-copy-summary-row shift-copy-summary-row-replace">
                    <div class="shift-copy-summary-head">
                        <span class="shift-copy-summary-title">Заменится</span>
                        <span class="shift-copy-summary-count">{{ formatDaysCount(replaceDates.length) }}</span>
                    </div>
                    <div class="shift-copy-summary-main">
                        <span>{{ formatDateRanges(replaceDates) }}</span>
                        <span class="shift-copy-muted">Текущие смены этих дней будут заменены</span>
                    </div>
                    <button
                        v-if="canExpandReplacements"
                        type="button"
                        class="shift-copy-summary-toggle"
                        :aria-expanded="isReplacementsOpen"
                        @click="isReplacementsOpen = !isReplacementsOpen">
                        {{ isReplacementsOpen ? 'Свернуть' : 'Что заменится' }}
                        <icon width="9" height="13" :class="isReplacementsOpen ? '-rotate-90' : 'rotate-90'"><IconChevronRight /></icon>
                    </button>
                    <div v-if="canExpandReplacements && isReplacementsOpen" class="shift-copy-summary-details">
                        <div v-for="group in replacementGroups" :key="group.key" class="shift-copy-summary-detail">
                            <span class="shift-copy-summary-detail-dates">{{ group.dates }}</span>
                            <span><s class="shift-copy-muted">{{ group.oldComposition }}</s> → {{ newComposition }}</span>
                        </div>
                    </div>
                </div>
            </div>
        </template>
    </div>
</template>

<script setup lang="ts">
import dayjs from 'dayjs'
import type { Dayjs } from 'dayjs'
import utc from 'dayjs/plugin/utc'
import { computed, ref } from 'vue'
import { useScheduleStore } from '@/stores/scheduleStore'
import AppCalendar from '@/ui/AppCalendar.vue'
import type { CalendarDayLabel } from '@/ui/calendar'
import IconChevronRight from '@/ui/icons/IconChevronRight.vue'
import { dayStateColor } from './weekSchedule'
import { useToast } from 'vue-toast-notification'
import type { CopyError, ShiftCopyPreviewResponse } from '@/api/schedule'
import type { Shift } from '@/types'
import { formatCopyError, formatShiftsCount, pluralRu } from './useShiftCopy'
import { ownRowShifts } from '@/utils/otherDepartmentShifts'

dayjs.extend(utc)

const store = useScheduleStore()
const toast = useToast({ position: 'bottom-right' })
const props = defineProps<{
    dt: string
    // Сохранённые смены исходного дня: копируются все, а не только выбранная карточка.
    sourceShifts: Shift[]
    mode: 'select' | 'confirm' | 'errors'
    errors: CopyError[]
    preview: ShiftCopyPreviewResponse['preview']
    isLoading: boolean
}>()
// Выбранные даты (YYYY-MM-DD, по возрастанию) хранит редактор: «Назад» и ошибка их не сбрасывают.
const modelValue = defineModel<string[]>({ required: true })

const sourceDay = computed(() => dayjs(props.dt))
const minimumTargetDate = computed(() => dayjs().add(1, 'day').startOf('day'))
const selectedDays = computed(() => modelValue.value.map(date => dayjs(date)))
const currentSchedule = computed(() => store.schedules.find(item => item.id === store.currentShift?.employment))
// Смены других подразделений и ТУ (RD-175/RD-176/RD-182) не занимают даты и не заменяются копированием.
const monthShifts = computed(() => (currentSchedule.value ? ownRowShifts(currentSchedule.value, store.currentDepartment?.id) : []).filter(shift => (
    shift.date && dayjs(shift.date).isSame(store.currentScheduleDate, 'month') && !shift.is_vacancy
)))
const hrDeviationDates = computed<Dayjs[]>(() => monthShifts.value
    .filter(shift => Boolean(shift.day_state_document))
    .map(shift => dayjs(shift.date)))
const hrDeviationLabels = computed<Record<string, string>>(() => monthShifts.value
    .filter(shift => Boolean(shift.day_state_document) && shift.date)
    .reduce<Record<string, string>>((labels, shift) => {
        const dayType = store.dayTypes.find(item => item.id === shift.day_type)
        if (dayType?.name) labels[dayjs(shift.date).format('YYYY-MM-DD')] = dayType.name
        return labels
    }, {}))
const occupiedDates = computed<Dayjs[]>(() => monthShifts.value
    .filter(shift => !shift.day_state_document)
    .map(shift => dayjs(shift.date)))
// Подсказка по уже загруженным сменам графика, не серверный список удалений.
const selectedOccupiedDates = computed(() => {
    const occupied = new Set(occupiedDates.value.map(date => date.format('YYYY-MM-DD')))
    return modelValue.value.filter(date => occupied.has(date))
})
const skippedEmployments = computed(() => props.preview?.employments.filter(item => !item.can_copy) ?? [])


const shiftTime = (value: string | null | undefined) => value ? dayjs.utc(value).format('HH:mm') : ''
const shiftLabel = (shift: Shift) => {
    const period = shift.plan_work_start_at && shift.plan_work_end_at
        ? `${shiftTime(shift.plan_work_start_at)}–${shiftTime(shift.plan_work_end_at)}`
        : (store.dayTypes.find(item => item.id === shift.day_type)?.name ?? 'без плана')
    return shift.is_overtime ? `доп. ${period}` : period
}
const compositionText = computed(() => {
    const labels = props.sourceShifts.map(shiftLabel)
    if (labels.length === 1) return `Копируется смена дня: ${labels[0]}`
    if (labels.length === 2) return `Копируются обе смены дня: ${labels[0]} и ${labels[1]}`
    const head = labels.slice(0, -1).join(', ')
    return `Копируются все ${formatShiftsCount(labels.length)} дня: ${head} и ${labels[labels.length - 1]}`
})

// --- RD-160: подписи ячеек и итог по уже загруженным сменам ТУ (без новых запросов) ---

const DATE_KEY = 'YYYY-MM-DD'
const sortShifts = (shifts: Shift[]) => [...shifts].sort((a, b) => (
    Number(Boolean(a.is_overtime)) - Number(Boolean(b.is_overtime))
    || (a.plan_work_start_at ?? '').localeCompare(b.plan_work_start_at ?? '')
))
// Рабочие смены дня (без КС) — по полной дате текущего месяца; occupiedDates задаёт только занятость.
const shiftsByDate = computed(() => {
    const result = new Map<string, Shift[]>()
    for (const shift of monthShifts.value) {
        if (shift.day_state_document || !shift.date) continue
        const key = dayjs(shift.date).format(DATE_KEY)
        result.set(key, [...(result.get(key) ?? []), shift])
    }
    for (const [key, shifts] of result) result.set(key, sortShifts(shifts))
    return result
})
const hrShiftByDate = computed(() => new Map(monthShifts.value
    .filter(shift => Boolean(shift.day_state_document) && shift.date)
    .map(shift => [dayjs(shift.date).format(DATE_KEY), shift] as const)))

const hasPlanTime = (shift: Shift) => Boolean(shift.plan_work_start_at && shift.plan_work_end_at)
// Компактно — часы без минут («20–08»); неизвестное время — «—», не часы макета.
const compactTime = (shift: Shift) => hasPlanTime(shift)
    ? `${dayjs.utc(shift.plan_work_start_at).format('HH')}–${dayjs.utc(shift.plan_work_end_at).format('HH')}`
    : '—'
const compactComposition = (shifts: Shift[]) => shifts.length === 1 ? compactTime(shifts[0]) : formatShiftsCount(shifts.length)
// Полно — «09:00–18:00, доп. 19:00–22:00»: все смены дня, включая доп./ночные.
const fullTime = (shift: Shift) => {
    const period = hasPlanTime(shift) ? `${shiftTime(shift.plan_work_start_at)}–${shiftTime(shift.plan_work_end_at)}` : '—'
    return shift.is_overtime ? `доп. ${period}` : period
}
const fullComposition = (shifts: Shift[]) => shifts.map(fullTime).join(', ')

const sourceComposition = computed(() => sortShifts(props.sourceShifts))
const newComposition = computed(() => fullComposition(sourceComposition.value))
const selectedSet = computed(() => new Set(modelValue.value))

const dayLabels = computed<Record<string, CalendarDayLabel>>(() => {
    const labels: Record<string, CalendarDayLabel> = {}
    const month = dayjs(store.currentScheduleDate)
    const sourceKey = sourceDay.value.format(DATE_KEY)
    for (let day = month.startOf('month'); day.isSame(month, 'month'); day = day.add(1, 'day')) {
        const key = day.format(DATE_KEY)
        if (key === sourceKey) {
            labels[key] = { text: 'источник' }
            continue
        }
        // Прошлые/недоступные по minDate дни — без подписей.
        if (day.isBefore(minimumTargetDate.value, 'day')) continue
        const hrShift = hrShiftByDate.value.get(key)
        if (hrShift) {
            // Фактический код DayType (до 5 символов) без сокращения; классификация КС — прежняя.
            const dayType = store.dayTypes.find(item => item.id === hrShift.day_type)
            if (dayType?.code) labels[key] = { text: dayType.code, background: dayStateColor(dayType.color) }
            continue
        }
        const dayShifts = shiftsByDate.value.get(key)
        if (selectedSet.value.has(key)) {
            labels[key] = { text: dayShifts?.length ? 'замена' : `+ ${compactComposition(sourceComposition.value)}` }
        } else if (dayShifts?.length) {
            labels[key] = { text: compactComposition(dayShifts) }
        }
    }
    return labels
})

const addDates = computed(() => modelValue.value.filter(date => !shiftsByDate.value.has(date)))
const replaceDates = computed(() => modelValue.value.filter(date => shiftsByDate.value.has(date)))
const formatDaysCount = (count: number) => `${count} ${pluralRu(count, ['день', 'дня', 'дней'])}`

const isReplacementsOpen = ref(false)
// Раскрытие только если состав каждого заменяемого дня известен на клиенте.
const canExpandReplacements = computed(() => replaceDates.value.length > 0
    && replaceDates.value.every(date => Boolean(shiftsByDate.value.get(date)?.length)))

// Соседние даты с одинаковым старым составом — одной строкой; неизвестное время не группируется.
const replacementGroups = computed(() => {
    const groups: { key: string, dates: string, oldComposition: string }[] = []
    let current: { dates: string[], composition: string, groupable: boolean } | null = null
    const flush = () => {
        if (!current) return
        groups.push({ key: current.dates[0], dates: formatDateRanges(current.dates), oldComposition: current.composition })
    }
    for (const date of [...replaceDates.value].sort()) {
        const shifts = shiftsByDate.value.get(date) ?? []
        const composition = fullComposition(shifts)
        const groupable = shifts.every(hasPlanTime)
        const previous = current?.dates[current.dates.length - 1]
        const isNext = Boolean(previous && dayjs(previous).add(1, 'day').isSame(date, 'day'))
        if (current && isNext && current.groupable && groupable && current.composition === composition) {
            current.dates.push(date)
        } else {
            flush()
            current = { dates: [date], composition, groupable }
        }
    }
    flush()
    return groups
})

// «2, 6, 9, 13, 16, 19–20 ноября»: подряд идущие дни сжаты в диапазон.
function formatDateRanges(dates: string[]) {
    const months = new Map<string, number[]>()
    for (const date of [...dates].sort()) {
        const value = dayjs(date)
        const month = value.format('D MMMM').split(' ')[1]
        const key = `${value.format('YYYY-MM')}|${month}`
        months.set(key, [...(months.get(key) ?? []), value.date()])
    }
    return [...months.entries()].map(([key, days]) => {
        const parts: string[] = []
        let start = days[0]
        let end = days[0]
        for (const day of [...days.slice(1), NaN]) {
            if (day === end + 1) {
                end = day
                continue
            }
            parts.push(start === end ? `${start}` : `${start}–${end}`)
            start = day
            end = day
        }
        return `${parts.join(', ')} ${key.split('|')[1]}`
    }).join(', ')
}

// «26, 27, 28 окт.»; даты разных месяцев — группами по месяцу.
function formatDateList(dates: string[]) {
    const groups = new Map<string, number[]>()
    for (const date of [...dates].sort()) {
        const value = dayjs(date)
        // «окт.» → «окт»: точку ставит сам текст там, где она нужна.
        const month = value.format('MMM').replace(/\.$/, '')
        groups.set(month, [...(groups.get(month) ?? []), value.date()])
    }
    return [...groups.entries()].map(([month, days]) => `${days.join(', ')} ${month}`).join('; ')
}

function onMultiSelect(value: Dayjs | [Dayjs, Dayjs] | Dayjs[]) {
    if (props.isLoading) return
    const dates = value as Dayjs[]
    if (dates.some(date => !date.isSame(store.currentScheduleDate, 'month'))) {
        toast.error('Для копирования доступны только даты текущего месяца')
    }
    modelValue.value = dates
        .filter(date => date.isSame(store.currentScheduleDate, 'month'))
        .map(date => date.format('YYYY-MM-DD'))
        .sort()
}
</script>

<style scoped>
.shift-copy {
    display: flex;
    flex-direction: column;
    gap: 12px;
    font-size: 12px;
    line-height: 140%;
    color: var(--color-text-main);
}

.shift-copy-muted {
    color: var(--color-text-muted);
}

.shift-copy-calendar-locked {
    opacity: .5;
    pointer-events: none;
}

.shift-copy-legend {
    display: flex;
    flex-wrap: wrap;
    gap: 8px 16px;
    font-size: 11px;
    color: var(--color-text-muted);
}

.shift-copy-legend span {
    display: inline-flex;
    align-items: center;
    gap: 6px;
}

.shift-copy-legend-mark {
    width: 14px;
    height: 14px;
    box-sizing: border-box;
    border-radius: 999px;
}

.shift-copy-legend-source {
    border: 1px solid var(--color-brand-main);
}

.shift-copy-legend-add {
    background-color: var(--color-accent-soft);
}

.shift-copy-legend-replace {
    border: 1px solid var(--color-warning);
    background-color: var(--color-accent-soft);
}

.shift-copy-legend-time,
.shift-copy-legend-chip {
    font-style: normal;
    font-size: 10px;
    font-weight: 500;
    font-variant-numeric: tabular-nums;
}

.shift-copy-legend-chip {
    padding: 0 4px;
    border-radius: 4px;
    background-color: var(--color-day-state-bg);
    font-weight: 600;
}

/* Итог над кнопкой: рамка 1 px bg-muted, radius-sm, 13 px; замены — warning-bg. */
.shift-copy-summary {
    border: 1px solid var(--color-bg-muted);
    border-radius: var(--radius-sm);
    overflow: hidden;
    font-size: 13px;
    line-height: 1.35;
}

.shift-copy-summary-row {
    display: grid;
    grid-template-columns: 96px minmax(0, 1fr) auto;
    gap: 4px 12px;
    padding: 10px 12px;
}

.shift-copy-summary-row + .shift-copy-summary-row {
    border-top: 1px solid var(--color-bg-muted);
}

.shift-copy-summary-row-replace {
    background-color: var(--color-warning-bg);
}

.shift-copy-summary-head,
.shift-copy-summary-main {
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
}

.shift-copy-summary-title {
    color: var(--color-text-muted);
}

.shift-copy-summary-count {
    font-weight: 600;
    color: var(--color-brand-main);
}

.shift-copy-summary-toggle {
    align-self: start;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 0;
    border: 0;
    background: transparent;
    font-family: inherit;
    font-size: 13px;
    font-weight: 600;
    color: var(--color-brand-main);
    cursor: pointer;
    white-space: nowrap;
}

.shift-copy-summary-toggle:focus-visible {
    outline: 2px solid var(--color-focus-border);
    outline-offset: 2px;
}

.shift-copy-summary-details {
    grid-column: 2 / -1;
    display: flex;
    flex-direction: column;
}

.shift-copy-summary-detail {
    display: grid;
    grid-template-columns: minmax(96px, auto) minmax(0, 1fr);
    gap: 12px;
    padding: 8px 0;
    border-top: 1px solid rgb(0 0 0 / 0.06);
    font-variant-numeric: tabular-nums;
}

.shift-copy-summary-detail-dates {
    font-weight: 600;
}

.shift-copy-error {
    display: flex;
    flex-direction: column;
    gap: 4px;
    padding: 10px 12px;
    border-radius: 4px;
    background-color: var(--color-status-chip-rejected-bg);
}

.shift-copy-error-title {
    font-size: 13px;
    font-weight: 600;
}

.shift-copy-confirm {
    display: flex;
    flex-direction: column;
    gap: 10px;
}

.shift-copy-confirm-title {
    font-size: 13px;
    font-weight: 600;
}

.shift-copy-confirm-counts {
    display: flex;
    gap: 24px;
    font-size: 13px;
    font-variant-numeric: tabular-nums;
}

.shift-copy-confirm-counts b {
    font-weight: 700;
}
</style>
